import {
    CardPointeBrowserTokenizationSessionType,
    CardPointeTokenizationResultType,
    tokenizeCardWithCardPointeCardSecure,
    CloverBrowserTokenizationSessionType,
    CloverTokenizationResultType,
    type GatewayCardTokenizationResponse,
    type GetDelegateTokenResponse,
} from '@anchorbase/pillar-sdk';
import { parseJsonWithNativeSafeNumbers } from '@anchorbase/pillar-sdk';
import {
    ValorBrowserTokenizationSessionSchema,
    ValorCardTokenizationResponseSchema,
} from '@anchorbase/pillar-sdk/schemas/ipg/valor/payment';
import type { CardFormData } from '../card-payment-entry';
import { buildValorCardTokenizationQueryParams } from './valor/card-tokenization';
import { tokenizeCardWithCloverEcommerce } from './clover/card-tokenization';

export type GatewayDelegateTokenizationFetch = (
    input: RequestInfo | URL,
    init?: RequestInit
) => Promise<Response>;

export interface GatewayDelegateTokenizationInput {
    readonly cardData: CardFormData;
    readonly delegateToken: GetDelegateTokenResponse;
    readonly fetchImplementation?: GatewayDelegateTokenizationFetch;
}

export type GatewayDelegateTokenizationResponse = GatewayCardTokenizationResponse;

export async function tokenizeCardWithGatewayDelegateToken(
    input: GatewayDelegateTokenizationInput
): Promise<GatewayDelegateTokenizationResponse> {
    const sessionType = input.delegateToken.browser_tokenization_session_type;
    switch (sessionType) {
        case undefined:
            return tokenizeCardWithValorDelegateToken(input);
        case CardPointeBrowserTokenizationSessionType:
            return await tokenizeCardWithCardPointeCardSecure({
                cardData: input.cardData,
                cardSecureBaseUrl: input.delegateToken.card_secure_base_url,
                fetchImplementation: input.fetchImplementation,
            });
        case CloverBrowserTokenizationSessionType:
            return await tokenizeCardWithCloverEcommerce({
                cardData: input.cardData,
                ecommerceApiKey: input.delegateToken.ecommerce_api_key,
                ecommerceTokenizationUrl:
                    input.delegateToken.ecommerce_tokenization_url,
                fetchImplementation: input.fetchImplementation,
            });
        default:
            throw new Error('Unsupported browser tokenization session type.');
    }
}

export function resolveGatewayTokenizationPaymentToken(
    response: GatewayDelegateTokenizationResponse
): string {
    if ('cardToken' in response) {
        return response.cardToken;
    }

    switch (response.result_type) {
        case CardPointeTokenizationResultType.CARDSECURE_BROWSER_TOKENIZATION:
            return response.cardsecure_response.token;
        case CardPointeTokenizationResultType.STORED_CARD_METADATA:
            return response.token;
        case CloverTokenizationResultType.ECOMMERCE_BROWSER_TOKENIZATION:
            return response.token_response.token;
        case CloverTokenizationResultType.STORED_CARD_METADATA:
            return response.token;
    }
}

export function resolveGatewayTokenizationCardBrand(input: {
    readonly response: GatewayDelegateTokenizationResponse;
    readonly fallbackCardBrand: string;
}): string {
    if ('card_brand' in input.response) {
        return input.response.card_brand;
    }

    if ('card_metadata' in input.response) {
        return input.response.card_metadata?.card_brand ?? input.fallbackCardBrand;
    }

    return input.fallbackCardBrand;
}

async function tokenizeCardWithValorDelegateToken(
    input: GatewayDelegateTokenizationInput
): Promise<GatewayDelegateTokenizationResponse> {
    const delegateToken = ValorBrowserTokenizationSessionSchema.parse(
        input.delegateToken
    );
    const normalizedPan = input.cardData.cardNumber.replace(/\D/g, '');
    const normalizedExpiryDate = input.cardData.expiryDate.replace('/', '');
    const queryParams = buildValorCardTokenizationQueryParams({
        delegateToken,
        normalizedPan,
        normalizedExpiryDate,
    });
    const fetchImplementation = input.fetchImplementation ?? fetch;

    const response = await fetchImplementation(
        `${delegateToken.domain}/?${queryParams.toString()}`,
        {
            method: 'POST',
            headers: {
                accept: 'application/json',
            },
        }
    );

    const responseBody: unknown = await parseJsonResponse(response);
    if (!response.ok) {
        throw new Error(`Card tokenization failed with HTTP ${response.status}.`);
    }

    const parseResult = ValorCardTokenizationResponseSchema.safeParse(responseBody);
    if (!parseResult.success) {
        throw new Error('Card tokenization response is missing expected fields.');
    }

    return parseResult.data;
}

async function parseJsonResponse(response: Response): Promise<unknown> {
    try {
        return parseJsonWithNativeSafeNumbers(await response.text());
    } catch {
        throw new Error('Card tokenization response was not valid JSON.');
    }
}
